import React from 'react';
import Modal from '../common/Modal';
import { 
    KeyIcon, 
    DocumentTextIcon, 
    IdentificationIcon, 
    CreditCardIcon, 
    BuildingLibraryIcon,
    PlusIcon 
} from '@heroicons/react/24/outline';

interface AddItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (itemType: string) => void;
    categoryId?: string;
}

const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, onClose, onSelect, categoryId }) => {
    const items = [
        { 
            type: 'password', 
            label: 'Password', 
            icon: KeyIcon,
            description: 'Store login credentials securely',
            gradient: 'from-blue-500 to-blue-600',
            bgGradient: 'from-blue-50 to-blue-100',
            hoverGradient: 'from-blue-100 to-blue-200'
        },
        { 
            type: 'cardDetails', 
            label: 'Card Details', 
            icon: CreditCardIcon,
            description: 'Save credit/debit card information',
            gradient: 'from-emerald-500 to-emerald-600',
            bgGradient: 'from-emerald-50 to-emerald-100',
            hoverGradient: 'from-emerald-100 to-emerald-200'
        },
        { 
            type: 'bankDetails', 
            label: 'Bank Details', 
            icon: BuildingLibraryIcon,
            description: 'Store bank account information',
            gradient: 'from-purple-500 to-purple-600',
            bgGradient: 'from-purple-50 to-purple-100',
            hoverGradient: 'from-purple-100 to-purple-200'
        },
        { 
            type: 'personalDetails', 
            label: 'Personal Details', 
            icon: IdentificationIcon,
            description: 'Keep personal information safe',
            gradient: 'from-rose-500 to-rose-600',
            bgGradient: 'from-rose-50 to-rose-100',
            hoverGradient: 'from-rose-100 to-rose-200'
        },
    ];

    const handleSelect = (type: string) => {
        onSelect(type);
        onClose();
    };

    const headerIcon = <PlusIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />;

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="Add New Item"
            headerIcon={headerIcon}
            headerDescription="Choose the type of item you want to add to your vault"
            size="xl"
        >
            <div className="space-y-4 sm:space-y-6">
                <div className="text-center">
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                        What would you like to add?
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600">
                        Select an item type to securely store your information
                    </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                    {items.map((item) => {
                        const IconComponent = item.icon;
                        return (
                            <button
                                key={item.type}
                                onClick={() => handleSelect(item.type)}
                                className={`group relative p-4 sm:p-6 lg:p-8 bg-gradient-to-br ${item.bgGradient} hover:${item.hoverGradient} rounded-xl sm:rounded-2xl lg:rounded-3xl border-2 border-transparent hover:border-white/50 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]`}
                            >
                                {/* Background decoration */}
                                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl sm:rounded-2xl lg:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                
                                <div className="relative flex flex-col items-center text-center space-y-2 sm:space-y-3 lg:space-y-4">
                                    {/* Icon with gradient background */}
                                    <div className={`p-2 sm:p-3 lg:p-4 bg-gradient-to-br ${item.gradient} rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110`}>
                                        <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="space-y-1 sm:space-y-2">
                                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 group-hover:text-gray-800 transition-colors">
                                            {item.label}
                                        </h3>
                                        <p className="text-xs sm:text-sm text-gray-600 group-hover:text-gray-700 transition-colors leading-relaxed px-1">
                                            {item.description}
                                        </p>
                                    </div>
                                    
                                    {/* Hover indicator - only show on larger screens */}
                                    <div className="hidden sm:flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
                                        <span className="text-xs lg:text-sm font-medium text-gray-700">
                                            Click to add
                                        </span>
                                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                                        <div className="w-2 h-1 bg-gray-300 rounded-full"></div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Footer info */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 border border-gray-200">
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                            </div>
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-1">
                                Secure & Encrypted
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                All your data is encrypted with industry-standard security. You can organize items into vaults and share them with team members when needed.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddItemModal;
